import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { PerformanceReviewQuestion } from '../api/generated/model/performanceReviewQuestion';
import { PerformanceReviewQuestionnaire } from '../api/generated/model/performanceReviewQuestionnaire';
import { isAnswered, isScaleScored } from '../components/review/QuestionField';

interface Params {
  isSelf: boolean;
  readOnly: boolean;
  questionnaire?: PerformanceReviewQuestionnaire;
  // Peer/manager questions snapshotted on the form itself.
  formQuestions?: PerformanceReviewQuestion[];
  formAnswers?: { questionId?: string | null }[];
  answers: Record<string, unknown>;
  t: TFunction;
}

export interface ReviewSection {
  key: string;
  title: string;
  questions: PerformanceReviewQuestion[];
}

// Derives the question model a review form renders from — the visible question
// list, its block sections, the "which comments are required because a rating was
// given" set, and progress counters. Pure (memoised) computation extracted from
// the form component so the container only wires state to view.
export function useReviewQuestionModel({
  isSelf,
  readOnly,
  questionnaire,
  formQuestions,
  formAnswers,
  answers,
  t,
}: Params) {
  const editableQuestions: PerformanceReviewQuestion[] = isSelf
    ? questionnaire?.selfForm?.questions || []
    : formQuestions || [];

  // For the read-only view, fall back to ALL known questions across the
  // questionnaire and keep only those the form actually answered — covers a subject
  // whose profession matches no peerForm, or a questionnaire edited after submit.
  const allKnownQuestions = useMemo(() => {
    const map = new Map<string, PerformanceReviewQuestion>();
    for (const qq of questionnaire?.selfForm?.questions || []) {
      if (qq.id) map.set(qq.id, qq);
    }
    for (const pf of questionnaire?.peerForms || []) {
      for (const qq of pf.questions || []) {
        if (qq.id && !map.has(qq.id)) map.set(qq.id, qq);
      }
    }
    for (const qq of formQuestions || []) {
      if (qq.id && !map.has(qq.id)) map.set(qq.id, qq);
    }
    return map;
  }, [questionnaire, formQuestions]);

  const questions: PerformanceReviewQuestion[] = useMemo(() => {
    if (!readOnly) return editableQuestions;
    const answered = (formAnswers || []).map((a) => a.questionId).filter((x): x is string => !!x);
    if (answered.length === 0) return editableQuestions;
    const seen = new Set<string>();
    const result: PerformanceReviewQuestion[] = [];
    for (const qq of editableQuestions) {
      if (qq.id) seen.add(qq.id);
      result.push(qq);
    }
    for (const qid of answered) {
      if (seen.has(qid)) continue;
      result.push(allKnownQuestions.get(qid) ?? { id: qid, text: qid, type: 'text' });
      seen.add(qid);
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, editableQuestions, formAnswers, allKnownQuestions]);

  const sections: ReviewSection[] = useMemo(() => {
    const groups = new Map<string, PerformanceReviewQuestion[]>();
    const order: string[] = [];
    for (const qq of questions) {
      const key = qq.block?.trim() || '__unnamed__';
      if (!groups.has(key)) {
        groups.set(key, []);
        order.push(key);
      }
      groups.get(key)!.push(qq);
    }
    return order.map((key) => ({
      key,
      title: key === '__unnamed__' ? t('perfReview.unnamedSection') : key,
      questions: groups.get(key)!,
    }));
  }, [questions, t]);

  // Once any scale question in a block is scored, that block's text (comment)
  // questions become required — a rating without justification isn't acceptable.
  const commentRequiredIds = useMemo(() => {
    const set = new Set<string>();
    for (const section of sections) {
      const scaleScored = section.questions.some(
        (qq) => qq.type === 'scale' && isScaleScored(answers[qq.id ?? '']),
      );
      if (!scaleScored) continue;
      for (const qq of section.questions) {
        if (qq.type === 'text' && qq.id) set.add(qq.id);
      }
    }
    return set;
  }, [sections, answers]);

  const { filledCount, totalCount, requiredRemaining } = useMemo(() => {
    let filled = 0;
    let requiredLeft = 0;
    for (const qq of questions) {
      const answered = isAnswered(qq, answers[qq.id ?? '']);
      const required = qq.required || commentRequiredIds.has(qq.id ?? '');
      if (answered) filled += 1;
      if (required && !answered) requiredLeft += 1;
    }
    return { filledCount: filled, totalCount: questions.length, requiredRemaining: requiredLeft };
  }, [questions, answers, commentRequiredIds]);

  const feedbackQuestionnaireEmpty = !isSelf && questions.length === 0;

  return {
    questions,
    sections,
    commentRequiredIds,
    filledCount,
    totalCount,
    requiredRemaining,
    feedbackQuestionnaireEmpty,
  };
}
